import React, { useState } from "react";
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import type { VisualizationBase } from "@models/VisualizationBase";
import { getVisualizationBaseSource } from "@models/VisualizationBase";
import CustomBaseContainer from "@components/charts/common/base-config/CustomBaseContainer";
import PreconfiguredBaseContainer from "@components/charts/common/base-config/PreconfiguredBaseContainer";
import DisableCustomBaseConfirmDialog from "@components/charts/common/base-config/DisableCustomBaseConfirmDialog";
import type { useQuestionRowBaseSelection } from "@components/charts/scorecard-config/hooks/useQuestionRowBaseSelection";
import QuestionRowBaseSection from "./QuestionRowBaseSection";

type ScorecardBaseMode = "all" | "custom" | "preconfigured" | "scorecard_row_base";

interface ScorecardBaseModeSectionProps {
  datasetId: string | null;
  isDisabled?: boolean;
  base: VisualizationBase;
  onChange: (base: VisualizationBase) => void;
  rowBaseSelection: ReturnType<typeof useQuestionRowBaseSelection>;
}

const SWITCH_TITLE: Record<ScorecardBaseMode, string> = {
  all: "Switch to All eligible respondents?",
  custom: "Switch to Custom base?",
  preconfigured: "Switch to Pre-configured base?",
  scorecard_row_base: "Switch to Scorecard Row Base?",
};

const ROW_BASE_SWITCH_MESSAGE =
  "Your Scorecard Row Base selection will be kept while you're editing, but the chart won't use it unless you switch back to Scorecard Row Base before saving.";

function deriveMode(
  base: VisualizationBase,
  rowBaseMode: ReturnType<typeof useQuestionRowBaseSelection>["mode"],
): ScorecardBaseMode {
  if (rowBaseMode !== "none") return "scorecard_row_base";
  if (!base.enabled) return "all";
  return getVisualizationBaseSource(base) === "preconfigured" ? "preconfigured" : "custom";
}

/** Unified Base control for a Scorecard Question row: one mutually-exclusive
 *  choice of All eligible respondents / Custom base / Pre-configured base
 *  (the generic VisualizationBase modes, shared with every other chart type
 *  via CustomBaseContainer/PreconfiguredBaseContainer) or Scorecard Row
 *  Base (brand-specific qualification, Scorecard-only). Selecting Scorecard
 *  Row Base clears the generic base and vice versa — a row can only use
 *  one Base mechanism at a time. */
const ScorecardBaseModeSection: React.FC<ScorecardBaseModeSectionProps> = ({
  datasetId,
  isDisabled,
  base,
  onChange,
  rowBaseSelection,
}) => {
  const [pendingTarget, setPendingTarget] = useState<ScorecardBaseMode | null>(null);

  const mode = deriveMode(base, rowBaseSelection.mode);
  const hasCustomBaseData = !!base.name.trim() || base.conditions.length > 0;
  const hasRowBaseData = rowBaseSelection.mode !== "none";

  const applyMode = (target: ScorecardBaseMode) => {
    if (target === "all") {
      onChange({ ...base, enabled: false });
      rowBaseSelection.setMode("none");
    } else if (target === "custom") {
      onChange({ ...base, enabled: true, source: "custom" });
      rowBaseSelection.setMode("none");
    } else if (target === "preconfigured") {
      onChange({ ...base, enabled: true, source: "preconfigured" });
      rowBaseSelection.setMode("none");
    } else {
      onChange({ ...base, enabled: false });
      rowBaseSelection.setMode("existing_question_row");
    }
  };

  const handleModeChange = (target: ScorecardBaseMode) => {
    if (target === mode) return;
    // Leaving a populated Custom base or a chosen Scorecard Row Base needs
    // the switch confirmation, same as the generic BaseModeSection.
    if (
      (mode === "custom" && hasCustomBaseData) ||
      (mode === "scorecard_row_base" && hasRowBaseData)
    ) {
      setPendingTarget(target);
      return;
    }
    applyMode(target);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Base
      </Typography>

      <RadioGroup
        value={mode}
        onChange={(e) => handleModeChange(e.target.value as ScorecardBaseMode)}
        sx={{ mt: 1 }}
      >
        <FormControlLabel
          value="all"
          control={<Radio size="small" />}
          label="All eligible respondents"
          disabled={isDisabled}
        />
        <FormControlLabel
          value="custom"
          control={<Radio size="small" />}
          label="Custom base"
          disabled={isDisabled || !datasetId}
        />
        <FormControlLabel
          value="preconfigured"
          control={<Radio size="small" />}
          label="Pre-configured base"
          disabled={isDisabled || !datasetId}
        />
        <FormControlLabel
          value="scorecard_row_base"
          control={<Radio size="small" />}
          label="Scorecard Row Base"
          disabled={isDisabled}
        />
      </RadioGroup>

      {mode === "custom" && (
        <CustomBaseContainer datasetId={datasetId} isDisabled={isDisabled} base={base} onChange={onChange} />
      )}
      {mode === "preconfigured" && (
        <PreconfiguredBaseContainer
          datasetId={datasetId}
          isDisabled={isDisabled}
          base={base}
          onChange={onChange}
        />
      )}
      {mode === "scorecard_row_base" && (
        <QuestionRowBaseSection rowBaseSelection={rowBaseSelection} />
      )}

      <DisableCustomBaseConfirmDialog
        open={pendingTarget !== null}
        title={pendingTarget ? SWITCH_TITLE[pendingTarget] : undefined}
        message={mode === "scorecard_row_base" ? ROW_BASE_SWITCH_MESSAGE : undefined}
        onCancel={() => setPendingTarget(null)}
        onConfirm={() => {
          if (pendingTarget) applyMode(pendingTarget);
          setPendingTarget(null);
        }}
      />
    </Box>
  );
};

export default ScorecardBaseModeSection;
